import * as Sentry from "@sentry/react-native"
import * as Updates from "expo-updates"
import { ClientError, GraphQLClient } from "graphql-request"
import { getSdk, type SdkFunctionWrapper } from "../generated/graphql"
import { getGraphQlUrl } from "@/config"
import { useLogout } from "@/queries/user"
import { $authToken } from "@/stores/auth"
import { getAppVersion } from "@/hooks/useAppVersion"
import { Platform } from "react-native"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractClientErrorMessage = (response: any) => {
  return (
    response.errors?.[0]?.message ?? `GraphQL Error (Code: ${response.status})`
  )
}

const withErrorLoggingWrapper: SdkFunctionWrapper = async <T>(
  action: () => Promise<T>,
  operationName: string,
  operationType?: string,
  variables?: any
): Promise<T> => {
  const { logout } = useLogout()
  try {
    const ts = Date.now()
    const result = await action()
    if (process.env.EXPO_PUBLIC_DEBUG_GRAPHQL) {
      console.log(
        `\x1b[45m\x1b[37m\x1b[1m[SDK]\x1b[0m ${operationName}:${operationType}: [${Date.now() - ts}ms] ${JSON.stringify(variables || "{}")}===`,
        result
      )
    }
    return result
  } catch (err) {
    if (err instanceof ClientError) {
      const query = err.request.query
      const graphQlException = new ClientError(err.response, err.request)
      graphQlException.message = err.message
      graphQlException.cause = err.cause
      graphQlException.name = `${err.name}: ${extractClientErrorMessage(graphQlException.response)}`
      graphQlException.stack = err.stack

      Sentry.withScope((scope) => {
        if (operationType) {
          scope.setTag("operation-type", operationType)
        }
        if (operationName) {
          scope.setTag("operation-name", operationName)
        }
        scope.setExtra("query", query)
        scope.setExtra("queryVariables", variables)

        scope.setFingerprint([
          "{{ default }}",
          String(operationType),
          String(operationName),
        ])

        Sentry.captureException(graphQlException)
      })

      if (
        err.message.includes("Signature has expired") ||
        err.message.includes("Invalid signature") ||
        err.message.includes("Signature verification failed")
      ) {
        void logout()
      }
    }
    throw err
  }
}

const getGlobalHeaders = () => {
  const { appVersionString } = getAppVersion()
  return {
    "X-Backflip-Mobile-User-Timezone":
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    "X-Backflip-Mobile-Runtime-Version": Updates.runtimeVersion || "web",
    "X-Backflip-Mobile-App-Version": appVersionString,
    "X-Backflip-Mobile-Platform": Platform.OS,
    "X-Backflip-Mobile-Update-Id": Updates.updateId || "dev",
    "X-Backflip-Mobile-Update-Created":
      Updates.createdAt?.toISOString() || new Date().toISOString(),
    "X-Backflip-Mobile-Update-Channel": Updates.channel || "dev",
  }
}

export const graphqlUrl = getGraphQlUrl()
export const client = new GraphQLClient(graphqlUrl, {
  headers: () => {
    const authToken = $authToken.get()
    if (!authToken) {
      return getGlobalHeaders()
    }
    return {
      ...getGlobalHeaders(),
      authorization: `jwt ${authToken}`,
    }
  },
})

export const sdk = getSdk(client, withErrorLoggingWrapper)

export default sdk
